import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { ResolvableServiceUsagesActionKind } from '../enums/action-kind.enum';
import type { ResolvableServiceUsagesState } from '../types/state.type';
import type { ResolvableServiceUsagesAction } from '../types/actions.type';
import type { GroupProps } from '../types/props.type';

function resolvableServiceUsagesReducer(
  state: ResolvableServiceUsagesState,
  action: ResolvableServiceUsagesAction,
): ResolvableServiceUsagesState {
  const actionType = action.type;

  switch (actionType) {
    case ResolvableServiceUsagesActionKind.ClearState:
      return (
        action.state ?? {
          idToCustomize: null,
          idToFlush: null,
          idToRemove: null,
          idToUpdate: null,
          isCreateDrawerOpen: false,
          isFlushAllDialogOpen: false,
          isFlushOneDialogOpen: false,
          isCustomizeAllDialogOpen: false,
          isCustomizeOneDialogOpen: false,
          isRemoveDialogOpen: false,
          isUpdateDrawerOpen: false,
        }
      );
    case ResolvableServiceUsagesActionKind.StartCreate:
      return {
        ...state,
        idToCustomize: null,
        idToFlush: null,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: true,
        isFlushAllDialogOpen: false,
        isFlushOneDialogOpen: false,
        isCustomizeAllDialogOpen: false,
        isCustomizeOneDialogOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case ResolvableServiceUsagesActionKind.StartFlushAll:
      return {
        ...state,
        idToCustomize: null,
        idToFlush: null,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isFlushAllDialogOpen: true,
        isFlushOneDialogOpen: false,
        isCustomizeAllDialogOpen: false,
        isCustomizeOneDialogOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case ResolvableServiceUsagesActionKind.StartFlushOne:
      return {
        ...state,
        idToCustomize: null,
        idToFlush: action.payload,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isFlushAllDialogOpen: false,
        isFlushOneDialogOpen: true,
        isCustomizeAllDialogOpen: false,
        isCustomizeOneDialogOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case ResolvableServiceUsagesActionKind.StartCustomizeAll:
      return {
        ...state,
        idToCustomize: null,
        idToFlush: null,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isFlushAllDialogOpen: false,
        isFlushOneDialogOpen: false,
        isCustomizeAllDialogOpen: true,
        isCustomizeOneDialogOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case ResolvableServiceUsagesActionKind.StartCustomizeOne:
      return {
        ...state,
        idToCustomize: action.payload,
        idToFlush: null,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isFlushAllDialogOpen: false,
        isFlushOneDialogOpen: false,
        isCustomizeAllDialogOpen: false,
        isCustomizeOneDialogOpen: true,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case ResolvableServiceUsagesActionKind.StartRemove:
      return {
        ...state,
        idToCustomize: null,
        idToFlush: null,
        idToRemove: action.payload,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isFlushAllDialogOpen: false,
        isFlushOneDialogOpen: false,
        isCustomizeAllDialogOpen: false,
        isCustomizeOneDialogOpen: false,
        isRemoveDialogOpen: true,
        isUpdateDrawerOpen: false,
      };
    case ResolvableServiceUsagesActionKind.StartUpdate:
      return {
        ...state,
        idToCustomize: null,
        idToFlush: null,
        idToRemove: null,
        idToUpdate: action.payload,
        isCreateDrawerOpen: false,
        isFlushAllDialogOpen: false,
        isFlushOneDialogOpen: false,
        isCustomizeAllDialogOpen: false,
        isCustomizeOneDialogOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: true,
      };
    case ResolvableServiceUsagesActionKind.EndCreate:
      return {
        ...state,
        isCreateDrawerOpen: false,
      };
    case ResolvableServiceUsagesActionKind.EndFlushAll:
      return {
        ...state,
        isFlushAllDialogOpen: false,
      };
    case ResolvableServiceUsagesActionKind.EndFlushOne:
      return {
        ...state,
        idToFlush: null,
        isFlushOneDialogOpen: false,
      };
    case ResolvableServiceUsagesActionKind.EndCustomizeAll:
      return {
        ...state,
        isCustomizeAllDialogOpen: false,
      };
    case ResolvableServiceUsagesActionKind.EndCustomizeOne:
      return {
        ...state,
        idToCustomize: null,
        isCustomizeOneDialogOpen: false,
      };
    case ResolvableServiceUsagesActionKind.EndRemove:
      return {
        ...state,
        idToRemove: null,
        isRemoveDialogOpen: false,
      };
    case ResolvableServiceUsagesActionKind.EndUpdate:
      return {
        ...state,
        idToUpdate: null,
        isUpdateDrawerOpen: false,
      };
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unknown action type: ${actionType}`);
  }
}

// In Manual grouping mode several group forms share the same routes: each one
// must only react to the routes that target it, otherwise every group opens
// its own dialog or drawer on top of the others. The creation route names its
// group.
const useRouteOwnership = (
  { isInGroup = false, showInheritanceAlert = true }: Readonly<GroupProps>,
  ownedServiceUsageIds: readonly Id[],
) => {
  const { serviceUsageId } = useParams();

  return {
    ownsServiceUsageRoute:
      !isInGroup ||
      ownedServiceUsageIds.some((id) => id.toString() === serviceUsageId),
    ownsServiceUserRoute: !isInGroup || showInheritanceAlert,
  };
};

export const useResolvableServiceUsagesReducer = (
  props: Readonly<GroupProps>,
  ownedServiceUsageIds: readonly Id[],
) => {
  const {
    isInGroup = false,
    paths,
    queryKeysToInvalidate,
    serviceUsagesGroupId,
  } = props;
  const { pathname: currentPath } = useLocation();
  const { serviceUsageId } = useParams();
  const queryClient = useQueryClient();

  const { ownsServiceUsageRoute, ownsServiceUserRoute } = useRouteOwnership(
    props,
    ownedServiceUsageIds,
  );

  const createPath =
    paths &&
    (isInGroup
      ? buildPath(paths.editNewInGroup, {
          serviceUsagesGroupId: serviceUsagesGroupId.toString(),
        })
      : paths.editNew);

  const resolvedState = useMemo(
    () => ({
      idToCustomize:
        currentPath === paths?.editCustomizeOne &&
        serviceUsageId &&
        ownsServiceUsageRoute
          ? (serviceUsageId as Id)
          : null,
      idToFlush:
        currentPath === paths?.editFlushOne &&
        serviceUsageId &&
        ownsServiceUsageRoute
          ? (serviceUsageId as Id)
          : null,
      idToRemove:
        currentPath === paths?.editDelete &&
        serviceUsageId &&
        ownsServiceUsageRoute
          ? (serviceUsageId as Id)
          : null,
      idToUpdate:
        currentPath === paths?.editModify &&
        serviceUsageId &&
        ownsServiceUsageRoute
          ? (serviceUsageId as Id)
          : null,
      isCreateDrawerOpen: !!createPath && currentPath === createPath,
      isFlushAllDialogOpen:
        currentPath === paths?.editFlushAll && ownsServiceUserRoute,
      isFlushOneDialogOpen:
        currentPath === paths?.editFlushOne && ownsServiceUsageRoute,
      isCustomizeAllDialogOpen:
        currentPath === paths?.editCustomizeAll && ownsServiceUserRoute,
      isCustomizeOneDialogOpen:
        currentPath === paths?.editCustomizeOne && ownsServiceUsageRoute,
      isRemoveDialogOpen:
        currentPath === paths?.editDelete && ownsServiceUsageRoute,
      isUpdateDrawerOpen:
        currentPath === paths?.editModify && ownsServiceUsageRoute,
    }),
    [
      createPath,
      ownsServiceUsageRoute,
      ownsServiceUserRoute,
      serviceUsageId,
      paths?.editDelete,
      paths?.editFlushAll,
      paths?.editFlushOne,
      paths?.editCustomizeAll,
      paths?.editCustomizeOne,
      paths?.editModify,
      currentPath,
    ],
  );

  const [state, dispatch] = useReducer(
    resolvableServiceUsagesReducer,
    resolvedState,
  );

  useEffect(() => {
    dispatch({
      type: ResolvableServiceUsagesActionKind.ClearState,
      state: resolvedState,
    });
  }, [resolvedState]);

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const invalidateQueries = useCallback(() => {
    queryKeysToInvalidate.forEach((queryKey) => {
      void queryClient.invalidateQueries({ queryKey });
    });
  }, [queryClient, queryKeysToInvalidate]);

  const handleClearState = useCallback(() => {
    paths
      ? void navigate(paths.edit)
      : dispatch({ type: ResolvableServiceUsagesActionKind.ClearState });
  }, [paths, navigate]);

  const handleStartCreate = useCallback(() => {
    createPath
      ? void navigate(createPath)
      : dispatch({ type: ResolvableServiceUsagesActionKind.StartCreate });
  }, [createPath, navigate]);

  const handleStartFlushAll = useCallback(() => {
    paths?.editFlushAll
      ? void navigate(paths.editFlushAll)
      : dispatch({ type: ResolvableServiceUsagesActionKind.StartFlushAll });
  }, [navigate, paths]);

  const handleStartFlushOne = useCallback(
    (id: Id) => {
      paths?.editFlushOne
        ? void navigate(buildPath(paths.editFlushOne, { serviceUsageId: id }))
        : dispatch({
            type: ResolvableServiceUsagesActionKind.StartFlushOne,
            payload: id,
          });
    },
    [navigate, paths],
  );

  const handleStartCustomizeAll = useCallback(() => {
    paths?.editCustomizeAll
      ? void navigate(paths.editCustomizeAll)
      : dispatch({
          type: ResolvableServiceUsagesActionKind.StartCustomizeAll,
        });
  }, [navigate, paths]);

  const handleStartCustomizeOne = useCallback(
    (id: Id) => {
      paths?.editCustomizeOne
        ? void navigate(
            buildPath(paths.editCustomizeOne, { serviceUsageId: id }),
          )
        : dispatch({
            type: ResolvableServiceUsagesActionKind.StartCustomizeOne,
            payload: id,
          });
    },
    [navigate, paths],
  );

  const handleStartRemove = useCallback(
    (id: Id) => {
      paths
        ? void navigate(buildPath(paths.editDelete, { serviceUsageId: id }))
        : dispatch({
            type: ResolvableServiceUsagesActionKind.StartRemove,
            payload: id,
          });
    },
    [navigate, paths],
  );

  const handleStartUpdate = useCallback(
    (id: Id) => {
      paths
        ? void navigate(buildPath(paths.editModify, { serviceUsageId: id }))
        : dispatch({
            type: ResolvableServiceUsagesActionKind.StartUpdate,
            payload: id,
          });
    },
    [navigate, paths],
  );

  const handleCreateSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La prestation a bien été créé.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: ResolvableServiceUsagesActionKind.EndCreate });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleFlushAllSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La liste a bien été supprimée.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: ResolvableServiceUsagesActionKind.EndFlushAll });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleFlushOneSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La prestation a bien été réinitialisée.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: ResolvableServiceUsagesActionKind.EndFlushOne });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleCustomizeAllSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La liste a bien été copiée.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: ResolvableServiceUsagesActionKind.EndCustomizeAll });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleCustomizeOneSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La prestation a bien été copiée.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: ResolvableServiceUsagesActionKind.EndCustomizeOne });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleRemoveSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La prestation a bien été supprimée.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: ResolvableServiceUsagesActionKind.EndRemove });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleUpdateSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La prestation a bien été mise à jour.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: ResolvableServiceUsagesActionKind.EndUpdate });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  return {
    state,
    dispatch,
    handleClearState,
    handleStartCreate,
    handleStartFlushAll,
    handleStartFlushOne,
    handleStartCustomizeAll,
    handleStartCustomizeOne,
    handleStartRemove,
    handleStartUpdate,
    handleCreateSuccess,
    handleFlushAllSuccess,
    handleFlushOneSuccess,
    handleCustomizeAllSuccess,
    handleCustomizeOneSuccess,
    handleRemoveSuccess,
    handleUpdateSuccess,
  };
};
